import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import type { CommunityOverviewDailyPoint } from "@community-analytics/shared";

import { cn } from "@shared/lib";

export interface ActivityChartProps {
  /**
   * Always exactly 7 entries — the API guarantees zero-filled days for the
   * trailing week so the x-axis is continuous. The component renders an
   * empty-state placeholder (without crashing) when every point's
   * messagesTotal is zero.
   */
  points: ReadonlyArray<CommunityOverviewDailyPoint>;
  emptyLabel: string;
  className?: string;
}

const HEIGHT = 200;

export function ActivityChart({
  points,
  emptyLabel,
  className,
}: ActivityChartProps) {
  const hasAny = points.some((p) => p.messagesTotal > 0);
  if (!hasAny) {
    return (
      <div
        className={cn(
          "flex items-center justify-center rounded-lg border border-dashed bg-background text-sm text-muted-foreground",
          className,
        )}
        style={{ height: HEIGHT }}
        role="status"
      >
        {emptyLabel}
      </div>
    );
  }
  // Format the date axis as "Mon", "Tue", … using UTC so the labels match
  // the API's UTC YYYY-MM-DD bucketing.
  const data = points.map((p) => ({
    date: p.date,
    label: weekdayLabel(p.date),
    messages: p.messagesTotal,
    active: p.activeMembers,
  }));
  return (
    <div className={cn("rounded-lg border bg-background p-2", className)}>
      <ResponsiveContainer width="100%" height={HEIGHT}>
        <LineChart
          data={data}
          margin={{ top: 8, right: 12, left: 0, bottom: 0 }}
        >
          <CartesianGrid
            strokeDasharray="2 4"
            stroke="hsl(var(--border))"
            vertical={false}
          />
          <XAxis
            dataKey="label"
            tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 11 }}
            axisLine={false}
            tickLine={false}
          />
          <YAxis
            tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 11 }}
            axisLine={false}
            tickLine={false}
            allowDecimals={false}
            width={32}
          />
          <Tooltip
            cursor={{ stroke: "hsl(var(--border))", strokeDasharray: "2 2" }}
            contentStyle={{
              background: "hsl(var(--background))",
              border: "1px solid hsl(var(--border))",
              borderRadius: "var(--radius)",
              fontSize: 12,
            }}
          />
          <Line
            type="monotone"
            dataKey="messages"
            stroke="hsl(var(--primary))"
            strokeWidth={2}
            dot={{ r: 3, fill: "hsl(var(--primary))" }}
            activeDot={{ r: 5 }}
            isAnimationActive={false}
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

function weekdayLabel(yyyyMmDd: string): string {
  const d = new Date(`${yyyyMmDd}T00:00:00Z`);
  return d.toLocaleDateString("en-US", { weekday: "short", timeZone: "UTC" });
}
